import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const AUDIT_IDENTITY_TTL_MS = 30000;

let auditUnavailable = false;
let warnedUnavailable = false;

let cachedIdentity = null;
let cachedIdentityAt = 0;

const auditWriteClient = generateClient();

const CreateAuditEvent = /* GraphQL */ `
  mutation CreateAuditEvent($input: CreateAuditEventInput!) {
    createAuditEvent(input: $input) {
      id
    }
  }
`;

const GetReservationSnapshot = /* GraphQL */ `
  query GetReservationSnapshot($id: ID!) {
    getReservation(id: $id) {
      id
      CompanyId
      carId
      originalCarId
      originalCarName
      originalCarPlate
      carSubstitutions
      status
      title
      start
      end
      pickupAt
      pickupLocation
      dropoffAt
      dropoffLocation
      reservationRef
      customerName
      customerEmail
      customerPhone
      flightNumber
      phoneNumbers
      extras
      depositAmount
      depositCurrency
      rentalAmount
      rentalCurrency
      platform
      senderEmail
      notes
      paymentStatus
      amountPaid
      approvalStatus
      updatedAt
    }
  }
`;

const GetCarSnapshot = /* GraphQL */ `
  query GetCarSnapshot($id: ID!) {
    getCar(id: $id) {
      id
      CompanyId
      name
      plate
      active
      fuelType
      transmission
      year
      category
      bodyType
      color
      imageKey
      startingPrice
      seats
      updatedAt
    }
  }
`;

const GetMaintenanceSnapshot = /* GraphQL */ `
  query GetMaintenanceSnapshot($id: ID!) {
    getMaintenance(id: $id) {
      id
      CompanyId
      carId
      title
      start
      end
      notes
      approvalStatus
      updatedAt
    }
  }
`;

const GetCompanySnapshot = /* GraphQL */ `
  query GetCompanySnapshot($id: ID!) {
    getCompany(id: $id) {
      id
      name
      country
      currencies
      pickupLocations
      dropoffLocations
      updatedAt
    }
  }
`;

const GetAccountingSnapshot = /* GraphQL */ `
  query GetAccountingSnapshot($id: ID!) {
    getAccountingEntry(id: $id) {
      id
      CompanyId
      date
      type
      category
      description
      amount
      currency
      carId
      reservationId
      notes
      paymentMethod
      rentalIncludes
      resolved
      resolvedAt
      submittedBySub
      submittedByEmail
      updatedAt
    }
  }
`;

const GetPriceFrameSnapshot = /* GraphQL */ `
  query GetPriceFrameSnapshot($id: ID!) {
    getPriceFrame(id: $id) {
      id
      CompanyId
      name
      startDate
      endDate
      currency
      isActive
      updatedAt
    }
  }
`;

const GetCarPriceSnapshot = /* GraphQL */ `
  query GetCarPriceSnapshot($id: ID!) {
    getCarPrice(id: $id) {
      id
      CompanyId
      frameId
      carId
      p1_2
      p3_4
      p5_10
      p11_20
      p20_plus
      updatedAt
    }
  }
`;

const GetAddonsPricingSnapshot = /* GraphQL */ `
  query GetAddonsPricingSnapshot($id: ID!) {
    getAddonsPricing(id: $id) {
      id
      CompanyId
      countryCode
      currency
      insuranceTiers
      insurancePerDay_1_5
      insurancePerDay_6_plus
      boosterSeatPerDay
      boosterSeatMax
      infantSeatPerDay
      infantSeatMax
      babySeatPerDay
      babySeatMax
      snowChainsPerDay
      snowChainsMax
      extraDriverPerDay
      extraDriverMax
      updatedAt
    }
  }
`;

const GetMemberSnapshot = /* GraphQL */ `
  query GetMemberSnapshot($id: ID!) {
    getMember(id: $id) {
      id
      CompanyId
      sub
      email
      role
      canEditCalendar
      updatedAt
    }
  }
`;

const GetEmailMessageSnapshot = /* GraphQL */ `
  query GetEmailMessageSnapshot($id: ID!) {
    getEmailMessage(id: $id) {
      id
      CompanyId
      senderEmail
      subject
      isRead
      dismissedAt
      assignedCarId
      assignedBySub
      assignedAt
      updatedAt
    }
  }
`;

const GetChangeRequestSnapshot = /* GraphQL */ `
  query GetChangeRequestSnapshot($id: ID!) {
    getChangeRequest(id: $id) {
      id
      CompanyId
      entity
      op
      targetId
      status
      reason
      submittedBySub
      submittedByEmail
      approvedBySub
      approvedAt
      createdAt
      updatedAt
    }
  }
`;

const SNAPSHOT_QUERIES = {
  Reservation: { query: GetReservationSnapshot, root: 'getReservation' },
  Car: { query: GetCarSnapshot, root: 'getCar' },
  Maintenance: { query: GetMaintenanceSnapshot, root: 'getMaintenance' },
  Company: { query: GetCompanySnapshot, root: 'getCompany' },
  AccountingEntry: { query: GetAccountingSnapshot, root: 'getAccountingEntry' },
  PriceFrame: { query: GetPriceFrameSnapshot, root: 'getPriceFrame' },
  CarPrice: { query: GetCarPriceSnapshot, root: 'getCarPrice' },
  AddonsPricing: { query: GetAddonsPricingSnapshot, root: 'getAddonsPricing' },
  Member: { query: GetMemberSnapshot, root: 'getMember' },
  EmailMessage: { query: GetEmailMessageSnapshot, root: 'getEmailMessage' },
  ChangeRequest: { query: GetChangeRequestSnapshot, root: 'getChangeRequest' },
};

const AUDIT_KIND_OVERRIDES = {
  proposeChange: { action: 'SUBMIT', entity: 'ChangeRequest' },
  approveChange: { action: 'APPROVE', entity: 'ChangeRequest' },
  rejectChange: { action: 'REJECT', entity: 'ChangeRequest' },
  approveReservation: { action: 'APPROVE', entity: 'Reservation' },
  rejectReservation: { action: 'REJECT', entity: 'Reservation' },
  approveMaintenance: { action: 'APPROVE', entity: 'Maintenance' },
  rejectMaintenance: { action: 'REJECT', entity: 'Maintenance' },
  markEmailRead: { action: 'MARK', entity: 'EmailMessage' },
  assignEmailToCar: { action: 'ASSIGN', entity: 'EmailMessage' },
  setUserGroup: { action: 'SET', entity: 'Member' },
  inviteEmployee: { action: 'INVITE', entity: 'Member' },
  deleteUser: { action: 'DELETE', entity: 'User' },
  startCompany: { action: 'CREATE', entity: 'Company' },
};

function queryTextFromDocument(query) {
  if (!query) return '';
  if (typeof query === 'string') return query;
  if (typeof query?.loc?.source?.body === 'string') return query.loc.source.body;
  return String(query);
}

function isMutationQuery(queryText) {
  return /^\s*(#.*\n\s*)*mutation\b/i.test(queryText || '');
}

function mutationFieldName(queryText) {
  const compact = String(queryText || '')
    .replace(/#[^\n]*/g, ' ')
    .replace(/\s+/g, ' ');

  const match = compact.match(/\bmutation\b[\s\S]*?\{\s*([A-Za-z_][A-Za-z0-9_]*)/i);
  return match?.[1] || null;
}

function sanitizeJson(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Number.isNaN(value)) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(sanitizeJson);

  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) {
      const next = sanitizeJson(value[key]);
      if (next !== undefined) out[key] = next;
    }
    return out;
  }

  return value;
}

function toAwsJson(value) {
  const clean = sanitizeJson(value);
  if (clean === undefined) return undefined;
  return JSON.stringify(clean);
}

function maybeJson(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function toAction(fieldName) {
  const name = String(fieldName || '');
  if (/^create[A-Z]/.test(name)) return 'CREATE';
  if (/^update[A-Z]/.test(name)) return 'UPDATE';
  if (/^delete[A-Z]/.test(name)) return 'DELETE';
  if (/^approve[A-Z]/.test(name)) return 'APPROVE';
  if (/^reject[A-Z]/.test(name)) return 'REJECT';
  if (/^assign[A-Z]/.test(name)) return 'ASSIGN';
  if (/^mark[A-Z]/.test(name)) return 'MARK';
  if (/^invite[A-Z]/.test(name)) return 'INVITE';
  if (/^set[A-Z]/.test(name)) return 'SET';
  if (/^submit[A-Z]/.test(name)) return 'SUBMIT';
  if (/^run[A-Z]/.test(name)) return 'RUN';
  return 'MUTATION';
}

function toEntity(fieldName) {
  const name = String(fieldName || '');
  const stripped = name
    .replace(/^(create|update|delete|approve|reject|assign|mark|invite|set|submit|run)/, '')
    .replace(/ByCompanyByCreatedAt$/, '')
    .replace(/ByCompanyByStart$/, '')
    .replace(/ByCarByStart$/, '')
    .replace(/ByStatusStart$/, '');

  return stripped || name || 'Unknown';
}

function resolveAuditKind(fieldName) {
  const override = AUDIT_KIND_OVERRIDES[fieldName];
  if (override) return override;
  return {
    action: toAction(fieldName),
    entity: toEntity(fieldName),
  };
}

function firstDefined(...values) {
  return values.find((v) => v !== undefined && v !== null && v !== '');
}

function shortId(value, head = 8) {
  const s = String(value || '').trim();
  if (!s) return '';
  if (s.length <= head + 4) return s;
  return `${s.slice(0, head)}...`;
}

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function extractCompanyId(variables, resultObj, identity, beforeSnapshot, afterSnapshot) {
  return firstDefined(
    variables?.input?.CompanyId,
    variables?.input?.companyId,
    variables?.CompanyId,
    resultObj?.CompanyId,
    beforeSnapshot?.CompanyId,
    afterSnapshot?.CompanyId,
    identity?.CompanyId
  );
}

function extractTargetId(fieldName, variables, resultObj) {
  const inferredFromMutationId = firstDefined(
    variables?.input?.id,
    variables?.id,
    variables?.input?.targetId,
    resultObj?.id
  );

  if (inferredFromMutationId) return String(inferredFromMutationId);

  if (/^setUserGroup/.test(fieldName)) return String(firstDefined(variables?.input?.sub, resultObj?.sub) || '');
  if (/^deleteUser/.test(fieldName)) return String(firstDefined(variables?.input?.sub, resultObj?.sub) || '');
  if (/^markEmailRead/.test(fieldName)) return String(firstDefined(variables?.emailId, resultObj?.id) || '');
  if (/^assignEmailToCar/.test(fieldName)) return String(firstDefined(variables?.emailId, resultObj?.id) || '');

  return '';
}

function labelFromSnapshot(entity, snapshot, targetId) {
  if (entity === 'Reservation') {
    const ref = snapshot?.reservationRef;
    const customer = snapshot?.customerName;
    const title = snapshot?.title;
    if (ref) return `Ref ${ref}`;
    if (customer) return customer;
    if (title) return title;
  }

  if (entity === 'Car') {
    const name = snapshot?.name;
    const plate = snapshot?.plate;
    if (name && plate) return `${name} (${plate})`;
    if (name) return name;
    if (plate) return plate;
  }

  if (entity === 'Maintenance') {
    if (snapshot?.title) return snapshot.title;
  }

  if (entity === 'Company') {
    if (snapshot?.name) return snapshot.name;
  }

  if (entity === 'AccountingEntry') {
    if (snapshot?.description) return snapshot.description;
    if (snapshot?.category) return snapshot.category;
  }

  if (entity === 'PriceFrame') {
    if (snapshot?.name) return snapshot.name;
  }

  if (entity === 'Member') {
    if (snapshot?.email) return snapshot.email;
    if (snapshot?.sub) return `User ${shortId(snapshot.sub)}`;
  }

  if (entity === 'EmailMessage') {
    if (snapshot?.subject) return snapshot.subject;
    if (snapshot?.senderEmail) return snapshot.senderEmail;
  }

  if (entity === 'ChangeRequest') {
    if (snapshot?.entity && snapshot?.op) return `${snapshot.entity} ${snapshot.op}`;
  }

  if (targetId) return `#${shortId(targetId)}`;
  return '';
}

async function fetchSnapshot(entity, id) {
  if (!id) return null;
  const config = SNAPSHOT_QUERIES[entity];
  if (!config) return null;

  try {
    const res = await auditWriteClient.graphql({
      query: config.query,
      variables: { id },
      authMode: 'userPool',
    });
    const row = res?.data?.[config.root] || null;
    return row ? sanitizeJson(row) : null;
  } catch {
    return null;
  }
}

function deriveAfterSnapshot(action, beforeSnapshot, afterSnapshot, variablesInput, resultObj) {
  if (action === 'DELETE') return null;
  if (afterSnapshot) return sanitizeJson(afterSnapshot);

  if (isPlainObject(resultObj)) {
    return sanitizeJson(resultObj);
  }

  if (isPlainObject(variablesInput) && isPlainObject(beforeSnapshot)) {
    return sanitizeJson({ ...beforeSnapshot, ...variablesInput });
  }

  if (isPlainObject(variablesInput)) {
    return sanitizeJson(variablesInput);
  }

  return null;
}

function computeChangedKeys(beforeSnapshot, afterSnapshot) {
  if (!isPlainObject(beforeSnapshot) || !isPlainObject(afterSnapshot)) return [];

  const ignored = new Set(['id', 'createdAt', 'updatedAt', '__typename']);
  const keys = Array.from(new Set([
    ...Object.keys(beforeSnapshot),
    ...Object.keys(afterSnapshot),
  ])).filter((key) => !ignored.has(key));

  return keys.filter((key) => {
    const b = sanitizeJson(beforeSnapshot[key]);
    const a = sanitizeJson(afterSnapshot[key]);
    return JSON.stringify(b) !== JSON.stringify(a);
  });
}

async function getIdentity() {
  const now = Date.now();
  if (cachedIdentity && now - cachedIdentityAt < AUDIT_IDENTITY_TTL_MS) {
    return cachedIdentity;
  }

  try {
    const { tokens } = await fetchAuthSession();
    const p = tokens?.idToken?.payload || {};
    cachedIdentity = {
      sub: p.sub || null,
      email: p.email || null,
      CompanyId: p['custom:CompanyId'] ?? p['custom:companyId'] ?? null,
      groups: Array.isArray(p['cognito:groups']) ? p['cognito:groups'] : [],
    };
  } catch {
    cachedIdentity = null;
  }

  cachedIdentityAt = now;
  return cachedIdentity;
}

function summaryText(action, entity, targetId, source, label) {
  const bits = [action, entity].filter(Boolean);
  let base = bits.join(' ');
  if (!base) base = 'Mutation';
  if (label) base += `: ${label}`;
  else if (targetId) base += ` (${shortId(targetId)})`;
  if (source) base += ` from ${source}`;
  return base;
}

function markAuditUnavailable(error) {
  const msg =
    error?.errors?.[0]?.message ||
    error?.message ||
    '';

  if (!/createAuditEvent|CreateAuditEventInput|AuditEvent/i.test(String(msg))) return;

  auditUnavailable = true;
  if (!warnedUnavailable) {
    warnedUnavailable = true;
    console.warn('[audit] AuditEvent schema is not available yet; mutation logging is disabled until backend deploy.');
  }
}

async function writeAuditEvent({
  source,
  fieldName,
  variables,
  resultObj,
  action: actionOverride,
  entity: entityOverride,
  targetId: targetIdOverride,
  beforeSnapshot = null,
  afterSnapshot = null,
}) {
  if (auditUnavailable) return;
  if (!fieldName || fieldName === 'createAuditEvent') return;

  const identity = await getIdentity();
  if (!identity?.sub && !identity?.email) return;

  const { action, entity } = actionOverride && entityOverride
    ? { action: actionOverride, entity: entityOverride }
    : resolveAuditKind(fieldName);

  const targetId =
    targetIdOverride ||
    extractTargetId(fieldName, variables, resultObj) ||
    null;

  const variablesInput = maybeJson(variables?.input ?? null);
  const effectiveAfterSnapshot = deriveAfterSnapshot(
    action,
    beforeSnapshot,
    afterSnapshot,
    variablesInput,
    resultObj
  );

  const companyId = extractCompanyId(
    variables,
    resultObj,
    identity,
    beforeSnapshot,
    effectiveAfterSnapshot
  );
  if (!companyId) return;

  const targetLabel = labelFromSnapshot(
    entity,
    effectiveAfterSnapshot || beforeSnapshot,
    targetId
  );
  const changedKeys = computeChangedKeys(beforeSnapshot, effectiveAfterSnapshot);

  const input = {
    CompanyId: String(companyId),
    actorSub: identity?.sub || null,
    actorEmail: identity?.email || null,
    actorGroups: toAwsJson(identity?.groups || []),
    source: source || null,
    operation: fieldName,
    entity,
    action,
    targetId: targetId || null,
    summary: summaryText(action, entity, targetId, source, targetLabel),
    requestPayload: toAwsJson(maybeJson(variables || null)),
    resultPayload: toAwsJson(maybeJson(resultObj || null)),
    metadata: toAwsJson({
      recordedAt: new Date().toISOString(),
      targetLabel: targetLabel || null,
      changedKeys,
      beforeSnapshot: beforeSnapshot || null,
      afterSnapshot: effectiveAfterSnapshot || null,
    }),
  };

  try {
    await auditWriteClient.graphql({
      query: CreateAuditEvent,
      variables: { input },
      authMode: 'userPool',
    });
  } catch (error) {
    markAuditUnavailable(error);
    if (!auditUnavailable) {
      console.warn('[audit] Failed to write audit event.', error);
    }
  }
}

export function createAuditedClient(source = 'console') {
  const client = generateClient();
  const originalGraphql = client.graphql.bind(client);

  client.graphql = async (options) => {
    const queryText = queryTextFromDocument(options?.query);
    const isMutation = isMutationQuery(queryText);
    const fieldName = isMutation ? mutationFieldName(queryText) : null;

    let auditPlan = null;

    if (
      !auditUnavailable &&
      isMutation &&
      fieldName &&
      fieldName !== 'createAuditEvent' &&
      (!options?.authMode || options.authMode === 'userPool')
    ) {
      try {
        const { action, entity } = resolveAuditKind(fieldName);
        const preTargetId = extractTargetId(fieldName, options?.variables || null, null);

        let beforeSnapshot = null;
        const shouldReadBefore = ['UPDATE', 'DELETE', 'APPROVE', 'REJECT', 'MARK', 'ASSIGN', 'SET'].includes(action);
        if (shouldReadBefore && preTargetId) {
          beforeSnapshot = await fetchSnapshot(entity, preTargetId);
        }

        auditPlan = {
          fieldName,
          action,
          entity,
          preTargetId,
          beforeSnapshot,
        };
      } catch {
        auditPlan = null;
      }
    }

    const result = await originalGraphql(options);

    try {
      if (!auditPlan) return result;
      if (auditUnavailable) return result;
      if (Array.isArray(result?.errors) && result.errors.length) return result;

      const resultObj = result?.data?.[auditPlan.fieldName];
      const targetId =
        extractTargetId(auditPlan.fieldName, options?.variables || null, resultObj) ||
        auditPlan.preTargetId ||
        null;

      let afterSnapshot = null;
      const shouldReadAfter = ['CREATE', 'UPDATE', 'APPROVE', 'REJECT', 'MARK', 'ASSIGN', 'SET'].includes(auditPlan.action);
      if (shouldReadAfter && targetId) {
        afterSnapshot = await fetchSnapshot(auditPlan.entity, targetId);
      }

      await writeAuditEvent({
        source,
        fieldName: auditPlan.fieldName,
        variables: options?.variables || null,
        resultObj,
        action: auditPlan.action,
        entity: auditPlan.entity,
        targetId,
        beforeSnapshot: auditPlan.beforeSnapshot,
        afterSnapshot,
      });
    } catch {
      // no-op: audit logging must never block normal behavior
    }

    return result;
  };

  return client;
}
